import React, { useContext, useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Input, Label, Button } from '../components/ui';

import { AuthContext } from '../context/authContext';

//import { useAuth } from '../hooks/useAuth';

const EntidadRegistroPage = () => {
    // Iconos SVG inline (sin dependencia externa)
    const LoaderIcon = (
        <svg className="w-16 h-16 text-[#00AEC3] mx-auto mb-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
    );
    
    const CheckCircleIcon = (
        <svg className="w-16 h-16 text-green-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    );
    
    const XCircleIcon = (
        <svg className="w-16 h-16 text-red-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    );

    const { login, isAuthenticated } = useContext(AuthContext);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    //const auth = useAuth();
    
    const [status, setStatus] = useState('validating'); // 'validating' | 'ready' | 'registering' | 'error'
    const [errorMessage, setErrorMessage] = useState('');
    const [entidadData, setEntidadData] = useState(null);
    const hasRegisteredRef = useRef(false); // Evita revalidación después de registro exitoso
    
    // Form states
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [formErrors, setFormErrors] = useState({});

    const token = searchParams.get('token');
    const idRed = searchParams.get('id_red');

    useEffect(() => {
        const validateToken = async () => {
            // No validar si ya está autenticado (AppRouter redirigirá)
            if (isAuthenticated) {
                return;
            }
            
            // Evitar revalidación si ya se registró exitosamente
            if (hasRegisteredRef.current) {
                return;
            }
            
            if (!token || !idRed) {
                setStatus('error');
                setErrorMessage('Token o ID de entidad no proporcionados');
                return;
            }

            try {
                const response = await fetch(`/api/entidad-requests/validate-token?token=${token}&id_red=${idRed}`);
                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || 'Token inválido o expirado');
                }

                setEntidadData(data);
                setStatus('ready'); // Mostrar formulario de registro

            } catch (error) {
                setStatus('error');
                setErrorMessage(error.message);
            }
        };

        validateToken();
    }, [token, idRed, isAuthenticated]);

    // Redirigir a inicio cuando el usuario se autentica
    /*useEffect(() => {
        if (isAuthenticated) {
            navigate('/inicio', { replace: true });
        }
    }, [isAuthenticated, navigate]);*/

    const handleRegister = async (e) => {
        e.preventDefault();
        setFormErrors({});
        
        // Validaciones
        const errors = {};
        if (!username.trim()) errors.username = 'El nombre de usuario es obligatorio';
        if (!password) errors.password = 'La contraseña es obligatoria';
        if (password.length < 6) errors.password = 'La contraseña debe tener al menos 6 caracteres';
        if (password !== confirmPassword) errors.confirmPassword = 'Las contraseñas no coinciden';
        
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        
        setStatus('registering');
        
        try {
            const response = await fetch('/api/entidad-requests/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token,
                    username,
                    password,
                    confirmPassword
                })
            });
            
            //const data = await response.json();
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Registration failed');
            }

            const { jwtToken, name, role, userId, entidad_id } = await response.json();
            
            // Login automático con los datos recibidos
            // AppRouter detectará el cambio de autenticación y redirigirá automáticamente
            /*auth?.login(
                data.name,
                data.jwtToken,
                data.role,
                data.userId,
                false, // mustChangePassword
                false, // isEspecial
                null,  // user_municipio (las entidades no tienen municipio asignado)
                data.entidad_id // entidadId (id_red de la entidad)
            );*/
            if (jwtToken) {
                hasRegisteredRef.current = true; // Marcar que ya se registró para evitar revalidación
                login(name, jwtToken, role, userId, false, false, null, entidad_id);
            }
            // No es necesario setStatus ni redirección manual - AppRouter detectará el cambio de autenticación
            
        } catch (error) {
            setStatus('ready');
            setErrorMessage(error.message);
        }
    };

    // Iconos para toggle de contraseña
    const EyeIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
    );
    
    const EyeSlashIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L6.228 6.228" />
        </svg>
    );

    const iconColorClass = 'text-gray-400 hover:text-gray-500';
    
    // Estados para mostrar/ocultar contraseñas
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Si ya está autenticado, mostrar loading mientras AppRouter redirige
    if (isAuthenticated) {
        return (
            <div className="flex min-h-screen flex-1 flex-col justify-start pt-20 px-6 py-12 lg:px-8 bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Iniciando sesión...</p>
                </div>
            </div>
        );
    }

    // Loading state mientras valida token
    if (status === 'validating') {
        return (
            <div className="flex min-h-screen flex-1 flex-col justify-start pt-20 px-6 py-12 lg:px-8 bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Validando token de registro...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (status === 'error') {
        return (
            <div className="flex min-h-screen flex-1 flex-col justify-start pt-20 px-6 py-12 lg:px-8 bg-gray-50 dark:bg-gray-900">
                <div className="sm:mx-auto sm:w-full sm:max-w-lg">
                    <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <h3 className="mt-6 text-lg font-medium text-gray-900 dark:text-white">Acceso Denegado</h3>
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                {errorMessage || "El enlace puede haber expirado o ser inválido."}
                            </p>
                            <div className="mt-6">
                                <a href="/solicitud-entidad" className="text-blue-600 hover:text-blue-500 font-medium">
                                    Solicitar acceso como entidad →
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Ready state - formulario de registro
    return (
        <div className="flex min-h-screen flex-1 flex-col justify-start pt-20 px-6 py-12 lg:px-8 bg-gray-50 dark:bg-gray-900">
            <div className="sm:mx-auto sm:w-full sm:max-w-lg">
                <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                    Registro de usuario - Entidad
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                    RED - {entidadData?.entidad_nombre || entidadData?.nombre_institucion || idRed}
                </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
                <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    
                    {errorMessage && (
                        <div className="mb-4 rounded-md bg-red-50 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-600">{errorMessage}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleRegister} className="space-y-6">
                        <div>
                            <Label htmlFor="username">Nombre de usuario</Label>
                            <div className="mt-2">
                                <Input
                                    id="username"
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="Ingrese su nombre de usuario"
                                    disabled={status === 'registering'}
                                    required
                                />
                            </div>
                            {formErrors.username && (
                                <p className="mt-2 text-sm text-red-600">{formErrors.username}</p>
                            )}
                        </div>
                        
                        <div>
                            <Label htmlFor="password">Contraseña</Label>
                            <div className="mt-2" style={{ position: 'relative' }}>
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Mínimo 6 caracteres"
                                    disabled={status === 'registering'}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className={`absolute inset-y-0 right-0 flex items-center pr-3 text-xl leading-none cursor-pointer focus:outline-none ${iconColorClass}`}
                                >
                                    {showPassword ? EyeSlashIcon : EyeIcon}
                                </button>
                            </div>
                            {formErrors.password && (
                                <p className="mt-2 text-sm text-red-600">{formErrors.password}</p>
                            )}
                        </div>
                        
                        <div>
                            <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
                            <div className="mt-2" style={{ position: 'relative' }}>
                                <Input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Repita su contraseña"
                                    disabled={status === 'registering'}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className={`absolute inset-y-0 right-0 flex items-center pr-3 text-xl leading-none cursor-pointer focus:outline-none ${iconColorClass}`}
                                >
                                    {showConfirmPassword ? EyeSlashIcon : EyeIcon}
                                </button>
                            </div>
                            {formErrors.confirmPassword && (
                                <p className="mt-2 text-sm text-red-600">{formErrors.confirmPassword}</p>
                            )}
                        </div>
                        
                        <Button
                            type="submit"
                            className="w-full"
                            disabled={status === 'registering'}
                        >
                            {status === 'registering' ? 'Registrando...' : 'Completar registro'}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EntidadRegistroPage;
